import {useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {EffectCreative} from 'swiper/modules';
import 'swiper/css/effect-creative';
import 'swiper/css';
import {useNavigate} from 'react-router';
import {img} from '@/utils/pluginImg.js';
import {getFaqsForFlow} from '@/constants/faqs.js';
import {parseAppZoneDate} from '@/utils/dateUtils';
import {POLICY_CONSTANTS} from '@/constants/booking';
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx';
import {Loader} from '@/customComponents/uiComponents/Loader.jsx';
import {DownloadAppReminderContent} from '@/customComponents/BookRideSteps/Components/downloadAppReminderContent.jsx';
import {AirportPerksBar, PrebookCompareCard, PopularQuestionsSection} from '@/customComponents/sharedComponents/bookingPerks.jsx';
import {isNearAirport} from '@/utils/airports';
import {comparePrices} from '@/utils/pricing.js';

// The CONFIRM page's success screen — shown for every booking that page creates (return
// booking, rebook, plain confirm). The edit page has its own, simpler screen; those two
// are the only variants. See SuccessPopup, which picks between them.
//
// The SAME creative card stack as the booking-received step (modalPages/bookingReceived.jsx):
// same three cards, same order, so the two screens read identically — including the MIDDLE
// card's Exclusive Offer / 25% OFF / "Add a return trip" CTA, which an initial booking gets
// here exactly as it does there. Only a RETURN leg swaps that for a greeting, because a
// return has no return to sell. (The greeting used to render in both cases, which dropped
// the offer from every initial booking confirmed on these pages.)
//
// Rendered standalone inside SuccessPopup's Modal — there is no BookRideSteps step system
// here, so every perk section runs in `forcePopup` mode (always opens its inline popup,
// never routes to a step) AND `portal` mode (the popup would otherwise be trapped inside
// the swiper card — see PopupHost).
//
// Order matters twice over: the pills render in this order AND the index doubles as the
// slide index (onSelect → slideTo(i)). Reordering here means reordering the slides.
const BR_TABS = [
    {key: 'perks', label: 'Airport Perks & FAQs'},
    {key: 'return', label: 'Return Trip'},
    {key: 'next', label: 'Next Steps'},
];

/**
 * Sliding segmented tabs (CMR control). Every card carries its OWN copy — with partial
 * slides visible the neighbours would otherwise show content with no header — and each
 * one drives the whole deck. `active` is the card's own key, not the deck's current slide.
 */
function BrTabs({active, onSelect}) {
    return (
        <div className="hopn-cmr hopn-br-tabs-wrap">
            <div className="hopn-cmr-tabs" role="tablist">
                <span
                    className="hopn-cmr-tab-indicator"
                    style={{transform: `translateX(${BR_TABS.findIndex((t) => t.key === active) * 100}%)`}}
                    aria-hidden="true"
                />
                {BR_TABS.map((t, i) => (
                    <button key={t.key} type="button" role="tab" aria-selected={active === t.key}
                            className={`hopn-cmr-tab ${active === t.key ? 'active' : ''}`}
                            onClick={() => onSelect(i)}>
                        {t.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// Short location label for the compare row: airport code › city › first address chunk.
const shortLabel = (dest) => {
    if (!dest) return '';
    // Prefer the airport IATA code when the point is AT/near an airport — its address
    // often reads "YYZ Terminal 1, Mississauga, ON…", so the city fallback would wrongly
    // show "Mississauga".
    const near = dest.lat && dest.lng ? isNearAirport(dest.lat, dest.lng) : false;
    if (near && near.code) return near.code;
    const code = dest.address?.match(/\(([A-Z]{3})\)/);
    if (code) return code[1];
    if (dest.city) return dest.city;
    return (dest.address || '').split(',')[0].trim();
};

const noStep = () => {};

export default function ConfirmBookingReceivedTabs({trip, onDone}) {
    const navigate = useNavigate();
    const [shareCopied, setShareCopied] = useState(false);
    // Swiper instance for the card deck — lets the pill tabs jump to a slide.
    const [brSwiper, setBrSwiper] = useState(null);

    // This deck is the confirm page's success screen for EVERY booking it creates —
    // a return booking, a rebook, or a plain confirm — so the return-specific wording is
    // switched off the trip itself rather than assumed. Everything else (the three cards,
    // the tabs, the layout) is identical in all cases.
    const isReturn = trip?.trip_type === 'ReturnBooking';
    const receivedTitle = isReturn ? 'Return Booking Received!' : 'Booking Received!';

    const tripDirection = trip?.trip_direction || 'others';
    // "Airport tips" wording is airport-only — a city-to-city trip keeps the generic heading.
    const isAirportTrip = tripDirection !== 'others';
    const faqs = getFaqsForFlow(tripDirection, {
        freeCancellationAvailable: POLICY_CONSTANTS.FREE_CANCELLATION_AVAILABLE,
        freeCancellationHours: POLICY_CONSTANTS.FREE_CANCELLATION_HOURS,
        freeEditsCount: POLICY_CONSTANTS.FREE_EDITS_COUNT,
        editableHoursBeforePickup: POLICY_CONSTANTS.EDITABLE_HOURS_BEFORE_PICKUP,
        waitingPickupFreeSeconds: POLICY_CONSTANTS.WAITING_PICKUP_FREE_SECONDS,
        waitingPickupRateCentsPerSecond: POLICY_CONSTANTS.WAITING_PICKUP_RATE_CENTS_PER_SECOND,
        waitingPickupMaxWaitSeconds: POLICY_CONSTANTS.WAITING_PICKUP_MAX_WAIT_SECONDS,
    });
    // Pre-tax net_total, and that same total without the online/coupon discounts —
    // both straight off the vehicle's breakdown (see utils/pricing.js).
    const {price: comparePrice, sharePrice: compareShare} = comparePrices(trip?.selected_vehicle || {});
    const pickup = shortLabel(trip?.origin);
    const dropoff = shortLabel(trip?.destination);

    const handleViewTrip = () => { if (trip?.id) navigate(`/rides/${trip.id}`); };

    /*
     * Return-trip offer copy — the RETURN reverses the original direction: a to-airport
     * booking returns FROM the airport, a from-airport one returns TO it, and a
     * city-to-city trip gets the reversed route spelled out. Copied verbatim from the
     * booking flow's own booking-received step so the two screens sell it identically.
     */
    const returnOfferText = tripDirection === 'to_airport'
        ? 'on your return trip from airport'
        : tripDirection === 'from_airport'
            ? 'on your return trip to airport'
            : (pickup && dropoff
                ? `on your return trip from ${dropoff} to ${pickup}`
                : 'on your return trip');

    // "Add a return trip" — straight to the return-booking page for the trip just
    // confirmed, which becomes the parent. Same in-app navigation the booking flow uses.
    const [isReturnLoading, setIsReturnLoading] = useState(false);
    const handleAddReturn = () => {
        if (!trip?.id) return;
        setIsReturnLoading(true);
        navigate(`/return-booking/${trip.id}`);
    };

    const handleShareTrip = async () => {
        const url = window.location.origin;
        const LOC = {month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true};
        // Resolved to a real instant, so the shared text quotes the same clock the sharer
        // sees everywhere else in the dashboard (their own zone), not raw Toronto digits.
        const pickupD = trip?.scheduled_at ? parseAppZoneDate(trip.scheduled_at) : null;
        const durMin = trip?.route?.duration_minutes || 0;
        const pickupText = pickupD && !isNaN(pickupD) ? pickupD.toLocaleString('en-US', LOC) : '';
        const arrivalText = pickupD && !isNaN(pickupD) && durMin
            ? new Date(pickupD.getTime() + durMin * 60000).toLocaleString('en-US', LOC) : '';
        const lines = ["🚘 I just booked my airport ride with Hop'n — it's fantastic!"];
        if (pickup && dropoff) lines.push(`📍 ${pickup} → ${dropoff}`);
        if (pickupText) lines.push(`🕒 Pickup: ${pickupText}`);
        if (arrivalText) lines.push(`🏁 Arrival: ${arrivalText}`);
        if (comparePrice) lines.push(`💰 Flat all-in price: $${comparePrice} — no surge, tip included, guaranteed pickup.`);
        lines.push("Book yours 👇");
        const text = lines.join("\n");
        try {
            if (navigator.share) {
                await navigator.share({title: "My Hop'n Ride", text, url});
            } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(`${text}\n${url}`);
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2000);
            }
        } catch (_) { /* user cancelled / unsupported */ }
    };

    const handleAddToCalendar = () => {
        const title = "Hop'n Airport Ride";
        let dates = '';
        if (trip?.scheduled_at) {
            // Toronto wall-clock, not UTC — see bookingReceived.jsx. Appending 'Z' put the
            // calendar event out by the zone offset.
            const start = parseAppZoneDate(trip.scheduled_at);
            if (start && !isNaN(start.getTime())) {
                const end = new Date(start.getTime() + 60 * 60 * 1000);
                const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
                dates = `&dates=${fmt(start)}/${fmt(end)}`;
            }
        }
        const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}${dates}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="hopn-booking-confirm">
            <div className="hopn-br-deck">
                {/* One close for the whole deck — the shared header with nothing but the cross,
                    on the deck rather than inside a card so it stays put while the cards move.
                    The wrapper carries .hopn-br-close (ModalHeader only reads extraClasses as a
                    token bag and builds its own class list, so custom names never reach the DOM).
                    Closing here is the same exit as Done — the modal has no dismissable state. */}
                <div className="hopn-br-close">
                    <ModalHeader
                        inline={true}
                        title=""
                        extraClasses="transparent"
                        totalSteps={0}
                        cancelButton={true}
                        cancelButtonType="icon"
                        showBackButton={false}
                        setStep={noStep}
                        closeModal={onDone}
                    />
                </div>

                <Swiper
                    modules={[EffectCreative]}
                    effect="creative"
                    // Open on the middle card (index 1) — it carries the "Return Booking
                    // Received!" headline, so the confirmation is what the passenger lands on,
                    // and being centred it gets a neighbour stacked on BOTH sides rather than a
                    // lopsided stack. Must match BR_TABS's order.
                    initialSlide={1}
                    grabCursor={true}
                    slideToClickedSlide={true}
                    speed={450}
                    longSwipesRatio={0.1}
                    // MUST stay false: creative overlaps the slides, so an auto height would be
                    // measured off the stack rather than the active card.
                    autoHeight={false}
                    watchSlidesProgress={true}
                    creativeEffect={{
                        limitProgress: 2,
                        // translate is a % of the SLIDE — and each slide spans the full modal —
                        // so the neighbour offset auto-scales with the modal/screen width.
                        prev: {translate: ['-20%', 0, -180], scale: 0.84, opacity: 0.9},
                        next: {translate: ['20%', 0, -180], scale: 0.84, opacity: 0.9},
                    }}
                    onSwiper={setBrSwiper}
                    onProgress={(s) => {
                        s.slides.forEach((slide) => {
                            slide.style.zIndex = String(Math.round(50 - Math.abs(slide.progress) * 5));
                        });
                    }}
                    className="hopn-br-swiper"
                >
                    {/* Card 1 — Airport Perks & FAQs. The perks bar IS this card's header (it
                        also opens the compare modal), so it sits above the tabs. */}
                    <SwiperSlide className="hopn-ride-latest">
                        <div className="hopn-br-card">
                            <div className="modalPages-spacing">
                                <AirportPerksBar price={comparePrice} pickup={pickup} dropoff={dropoff}
                                                 sharePrice={compareShare} setStep={noStep} spacing={false}
                                                 compact forcePopup portal/>

                                <BrTabs active="perks" onSelect={(i) => brSwiper?.slideTo(i)}/>

                                <div className="hopn-br-panel hopn-ride-latest">
                                    <PopularQuestionsSection faqs={faqs} setStep={noStep} spacing={false} forcePopup portal
                                                             title={isAirportTrip ? 'Exclusive Airport Pickup Perks' : undefined}
                                                             chip={isAirportTrip ? 'Airport tips & FAQs' : ''}/>
                                    <PrebookCompareCard price={comparePrice} pickup={pickup} dropoff={dropoff}
                                                        sharePrice={compareShare} setStep={noStep} spacing={false} forcePopup portal/>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>

                    {/* Card 2 — the confirmation itself. Where the booking step sells a return
                        trip, this card greets instead: same headline, same tabs, same three-part
                        offer layout (heading · chip · mint pill) so the deck's rhythm holds. */}
                    <SwiperSlide>
                        <div className="hopn-br-card">
                            <div className="modalPages-spacing">
                                <div className="hopn-br-head">
                                    {/* Title sits in a white pill on the mint banner. */}
                                    <h3 className="hopn-br-badge">
                                        <span className="hopn-br-head-icon">
                                            <img decoding="async" src={img('green-tick.svg')} alt=""/>
                                        </span>
                                        <span className="hopn-br-dot" aria-hidden="true"></span>
                                        {receivedTitle}
                                    </h3>

                                    <div className="hopn-br-head-row">
                                        <p>We're matching you with the best driver. Confirmation coming shortly<span className="hopn-br-dots"><span></span><span></span><span></span></span></p>
                                        {/* Status badge, not a control — the match runs on its own. */}
                                        <span className="hopn-br-finding">Finding driver<span className="hopn-br-dots"><span></span><span></span><span></span></span></span>
                                    </div>
                                </div>

                                <BrTabs active="return" onSelect={(i) => brSwiper?.slideTo(i)}/>

                                <div className="hopn-br-panel">
                                    {/*
                                      * WHICH BOOKING was just confirmed decides this card.
                                      *
                                      * A RETURN leg has nothing left to sell — it IS the return — so it
                                      * gets the greeting. Anything else (an initial booking, a rebook) is
                                      * exactly the case the booking flow sells a return on, so it gets the
                                      * booking step's own Exclusive Offer / 25% OFF / "Add a return trip"
                                      * block. This card used to greet in BOTH cases, which quietly dropped
                                      * the return offer from every initial booking confirmed here.
                                      *
                                      * Both variants reuse .hopn-br-offer (heading / chip / mint pill), so
                                      * the deck's rhythm holds either way and no new CSS is needed.
                                      */}
                                    {isReturn ? (
                                        <>
                                            <div className="hopn-br-offer">
                                                <span className="hopn-br-offer-tag">You're all set!</span>
                                                <span className="hopn-br-offer-sub">
                                                    {pickup && dropoff
                                                        ? `Return trip ${pickup} → ${dropoff}`
                                                        : 'Your return trip is confirmed'}
                                                </span>
                                                <span className="hopn-br-offer-amt">Thank you!</span>
                                            </div>

                                            <p className="hopn-br-note">Everything's confirmed — we'll take it from here.</p>

                                            {/* Done lives INSIDE the card, not under the deck: the shell
                                                behind the cards is transparent, so anything outside a card
                                                would float over the blurred page. GREETING VARIANT ONLY —
                                                the offer variant ends on "Add a return trip", exactly as the
                                                booking flow's own card does, and a second black pill under it
                                                just competed with the CTA. That variant's exit is the ✕ on
                                                the first slide, which already calls onDone. */}
                                            <button type="button" onClick={onDone}
                                                    className="cursor-pointer w-full py-[15px] mt-4 rounded-full bg-(--text-primary) text-white font-bold text-base flex items-center justify-center min-h-[55px] border-0">
                                                Done
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="hopn-br-offer">
                                                <span className="hopn-br-offer-tag">Exclusive Offer</span>
                                                <span className="hopn-br-offer-sub">{returnOfferText}</span>
                                                <span className="hopn-br-offer-amt">25% OFF</span>
                                            </div>

                                            <button
                                                type="button"
                                                className="hopn-br-return-cta"
                                                onClick={handleAddReturn}
                                                disabled={isReturnLoading}
                                            >
                                                {isReturnLoading ? <Loader color="#fff"/> : (
                                                    <>
                                                        Add a return trip
                                                        <span className="hopn-br-return-arrow" aria-hidden="true">
                                                            <img decoding="async" src={img('arrow-right.svg')} alt=""/>
                                                        </span>
                                                    </>
                                                )}
                                            </button>

                                            <p className="hopn-br-note">Save 25% instantly when scheduling now</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>

                    {/* Card 3 — Next Steps: status banner + download-app + actions. `is-plain`
                        drops the black pill so "Finding driver" reads as quiet status text, and
                        the badge itself carries the status on its right (same as the booking step). */}
                    <SwiperSlide>
                        <div className="hopn-br-card">
                            <div className="modalPages-spacing">
                                <div className="hopn-br-head is-plain">
                                    <h3 className="hopn-br-badge">
                                        <span className="hopn-br-head-icon">
                                            <img decoding="async" src={img('green-tick.svg')} alt=""/>
                                        </span>
                                        <span className="hopn-br-dot" aria-hidden="true"></span>
                                        {receivedTitle}
                                        <span className="hopn-br-finding">Finding driver<span className="hopn-br-dots"><span></span><span></span><span></span></span></span>
                                    </h3>

                                    <div className="hopn-br-head-row">
                                        <p>We're matching you with the best driver. Confirmation coming shortly<span className="hopn-br-dots"><span></span><span></span><span></span></span></p>
                                    </div>
                                </div>

                                <BrTabs active="next" onSelect={(i) => brSwiper?.slideTo(i)}/>

                                <div className="hopn-br-panel">
                                    <div className="hopn-br-next-card">
                                        <DownloadAppReminderContent showSubtitle={false}/>
                                        <div className="hopn-br-next-actions">
                                            <button type="button" onClick={handleViewTrip}>
                                                <img decoding="async" src={img('small-bold-pencil.svg')} alt=""/>
                                                My Trip
                                            </button>
                                            <button type="button" onClick={handleShareTrip}>
                                                <img decoding="async" src={img('small-bold-share.svg')} alt=""/>
                                                {shareCopied ? 'Link copied!' : 'Share trip'}
                                            </button>
                                            <button type="button" onClick={handleAddToCalendar}>
                                                <img decoding="async" src={img('small-bold-calender.svg')} alt=""/>
                                                Add to calendar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>

                    {/* Click-to-activate hit zones — the gutters either side of the centred card,
                        i.e. exactly where the neighbour peeks out. Explicit targets because every
                        slide shell spans the FULL modal width, so which element a gutter click
                        lands on depends on stacking order, not on which card is visible there.
                        slot="container-end" keeps them inside the swiper container so dragging
                        the gutter still works — only the click is intercepted. */}
                    <button slot="container-end" type="button" aria-label="Previous card"
                            className="hopn-br-peek hopn-br-peek--prev"
                            onClick={() => brSwiper?.slidePrev()}/>
                    <button slot="container-end" type="button" aria-label="Next card"
                            className="hopn-br-peek hopn-br-peek--next"
                            onClick={() => brSwiper?.slideNext()}/>
                </Swiper>
            </div>
        </div>
    );
}
